import {
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  HorizontalOriginOption, OriginOptions, TabStyles, VerticalOriginOption
} from '@tableau/widgets';
import React from 'react';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';

import { Hover } from '../Components/Hover';

export type TooltipDelayType = number | 'none';

export interface TooltipAnchorProps {
  readonly tooltipContent: React.ComponentType<{}>;
  readonly width?: number;
  readonly title: string;
  readonly onShowTooltip?: () => void;
  readonly delay?: number;
  readonly disableAutoFocus?: boolean; // for cases when there is no focusable content in the tooltip
  readonly anchorOrigin?: OriginOptions;
  readonly useKeyboardToActivateTooltip?: boolean;
  readonly disableTooltip?: boolean;
}

export interface TooltipAnchorState {
  readonly showingTooltip: boolean;
}

const DefaultAnchorOrigin: OriginOptions = {
  horizontal: HorizontalOriginOption.right,
  vertical: VerticalOriginOption.centerY
};

export class TooltipAnchor extends React.PureComponent<TooltipAnchorProps, TooltipAnchorState> {
  private anchorElement: HTMLElement;
  private tooltipContainerElementRef: React.RefObject<HTMLDivElement> = React.createRef();
  private triggerTooltipTimeout: NodeJS.Timeout;
  private anchorOrTooltipHasFocus = false;
  private alreadyOpenForFocus = false;

  constructor(props: TooltipAnchorProps) {
    super(props);
    this.state = { showingTooltip: false };
  }

  public render() {
    return (
      <div ref={this.setAnchorElementRef}>
        <Hover groupName="content-tooltip"
          enterDelay={this.props.delay >= 0 ? this.props.delay : 100}
          onEnter={this.showTooltip}
          onLeave={this.hideTooltip}
          leaveOnScroll={true}>
          {this.state.showingTooltip && !this.props.disableTooltip && !ReactModalService.isAnyModalOpen() && this.renderTooltip()}
          {this.props.children}
        </Hover>
      </div>
    );
  }

  public componentDidMount() {
    if (this.props.useKeyboardToActivateTooltip) {
      document.addEventListener('focusin', this.documentFocusInHandler);
    }
  }

  private documentFocusInHandler = () => {
    // "anchorOrTooltipHasFocus" indicates whether the focus is inside the tooltip anchor itself or in the tooltip content.
    // Sometimes there are multiple focusable elements in the tooltip anchor and content.
    // No matter which focusable element has the focus, "anchorOrTooltipHasFocus" is true.
    this.anchorOrTooltipHasFocus = this.anchorElement.contains(document.activeElement) || this.tooltipContentHasFocus();



    if (!this.anchorOrTooltipHasFocus) {
      // If the focus totally goes out of the tooltip anchor and tooltip content, hide the tooltip.
      this.hideTooltip();
      this.alreadyOpenForFocus = false;
      clearTimeout(this.triggerTooltipTimeout);
    } else {
      this.triggerTooltipTimeout = setTimeout(() => {
        // Since there can be multiple focusable elements inside tooltip anchor, only for the first time one of the focusable element
        // gets the focus, show the tooltip and mark "alreadyOpenForFcous" to be true to avoid showing the tooltip again when another focusable element
        // inside the tooltip anchor gets focus right after the previous focusable element inside tooltip anchor gets blurred.
        if (this.anchorOrTooltipHasFocus && !this.alreadyOpenForFocus) {
          this.showTooltip();
          this.alreadyOpenForFocus = true;
        }
      }, 2000);
    }
  }

  private tooltipContentHasFocus() {
    let result = false;

    let tooltipContainer = this.tooltipContainerElementRef.current as Element;
    if (tooltipContainer) {
      // In Mac Safari, when a focusable element inside tooltip content is clicked and triggering the tooltip to be hidden, at that moment,
      // The dialog element which hosts the tooltip content gets focused.
      // In this case, consider the dialog element as the tooltip content element.
      if (tooltipContainer.closest) {
        const dialogElement = tooltipContainer.closest('[data-tb-test-id="content-info-overlay-Dialog-Content"]') as Element;
        if (dialogElement) {
          tooltipContainer = dialogElement;
        }
      }
      result = tooltipContainer.contains(document.activeElement);
    }

    return result;
  }

  private renderTooltip() {
    const TooltipContent = this.props.tooltipContent;
    const widthWithDialogPadding = this.props.width + 2 * TabStyles.Spacing.LargeSpace;

    return (
      <DialogWidget type={DialogType.Uber}
        testId="content-info-overlay"
        dialogWidth={widthWithDialogPadding}
        showCloseButton={false}
        onCloseRequest={this.hideTooltip}
        positioningBehavior={DialogPositioningBehavior.Anchored}
        anchorOrigin={this.props.anchorOrigin || DefaultAnchorOrigin}
        isModal={false}
        anchorEl={this.anchorElement}
        showFooter={false}
        shouldReturnFocusOnClose={this.props.useKeyboardToActivateTooltip}
        disableAutoFocus={this.props.disableAutoFocus}
        title={this.props.title}>
        <div ref={this.tooltipContainerElementRef}>
          <TooltipContent />
        </div>
      </DialogWidget>
    );
  }

  public componentWillUnmount() {
    if (this.props.useKeyboardToActivateTooltip) {
      document.removeEventListener('focusin', this.documentFocusInHandler);
    }

    this.anchorElement = null;
  }

  private setAnchorElementRef = (element: HTMLElement) => {
    this.anchorElement = element;
  }

  private showTooltip = () => {
    this.anchorElement && this.setState({ showingTooltip: true });
    if (this.props.onShowTooltip) {
      this.props.onShowTooltip();
    }
  }

  private hideTooltip = () => {
    clearTimeout(this.triggerTooltipTimeout);
    this.anchorElement && this.setState({ showingTooltip: false });
  }
}
